import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { dashboardHref } from 'helpers/dashboardSession';

const DASHBOARD_HREF = dashboardHref('nav');
const DASHBOARD_LABEL = 'Dashboard';
const DASHBOARD_ARIA_LABEL = 'View Dashboard';
const DASHBOARD_SKELETON_WIDTH_CLASS = 'w-[84px]';

const isDashboardPath = (slug: string): boolean => {
  const normalizedSlug = slug.replace(/^\/+/, '');
  return normalizedSlug === 'dashboard' || normalizedSlug.startsWith('dashboard/');
};

interface DashboardNavItemProps {
  slug: string;
}

export const DashboardNavItem: React.FC<DashboardNavItemProps> = ({ slug }) => {
  const { authentication } = useAuth();
  const isDashboardActive = isDashboardPath(slug);

  if (authentication.state === 'authenticating') {
    return <DashboardNavSkeleton />;
  }

  if (authentication.state !== 'authenticated') {
    return null;
  }

  return (
    <Box role="listitem" className="relative block pr-2">
      <Box
        href={DASHBOARD_HREF}
        aria-label={DASHBOARD_ARIA_LABEL}
        aria-current={isDashboardActive ? 'page' : undefined}
        analyticsName="cosmos-mega-nav-dashboard"
        className="inline-flex h-10 min-w-[84px] items-center justify-center rounded-xl px-3 py-2 font-medium text-od-content-primary no-underline hover:bg-od-bg-hover-primary"
      >
        <Text as="span" className="text-label-md text-inherit">
          {DASHBOARD_LABEL}
        </Text>
      </Box>
    </Box>
  );
};

const DashboardNavSkeleton: React.FC = () => {
  return (
    <Box role="listitem" aria-hidden className="relative block pr-2">
      <Box
        data-dashboard-nav-skeleton
        className="inline-flex h-10 min-w-[84px] items-center justify-center px-3 py-2"
      >
        <Box
          className={`skeleton h-4 ${DASHBOARD_SKELETON_WIDTH_CLASS} rounded-md bg-od-bg-tertiary`}
        />
      </Box>
    </Box>
  );
};
